import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import { useSubscriptionApi } from "src/_common/hooks/actions/subscription/appSubscriptionApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useAppUserDetailsSelector, useAppUserPermissionsSelector } from "src/_common/hooks/selectors/userSelector";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { hasPermission } from "src/_common/utils/permissionsUtils";
import SubscriptionPlansGrid from "./SubscriptionPlansGrid";
import CancelSubscriptionModal from "src/components/modal/CancelSubscriptionModal";
import moment from "moment";

interface SubscriptionsMainProps {}

const SubscriptionsMain: React.FC<SubscriptionsMainProps> = () => {
  const history = useHistory();
  const userApi = useUserApi();
  const subscriptionApi = useSubscriptionApi();
  const toast = useToaster();
  const userSelector = useAppUserDetailsSelector();
  const { t } = useTranslation("payment");
  const userDetails = useAppUserDetailsSelector();
  const userPermissions = useAppUserPermissionsSelector();

  const [currentPlanData, setCurrentPlanData] = useState<any>(null);
  const [subscriptionPlanData, setSubscriptionPlanData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">("yearly");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showRecurringTooltip, setShowRecurringTooltip] = useState(false);

  // Ref for scrolling to other plans section
  const otherPlansRef = React.useRef<HTMLDivElement>(null);
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  // useEffect(() => {
  //   // Get the current subscription plan from user data
  //   console.log('User subscription plan data:', userSelector?.user?.subscription_plan);
  //   if (userSelector?.user?.subscription_plan) {
  //     setCurrentPlan(userSelector.user.subscription_plan);
  //   }
  // }, [userSelector]);

  // Fetch subscription plans from API
  useEffect(() => {
    loadSubscriptionPlans();
  }, []);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setShowRecurringTooltip(false);
      }
    };

    if (showRecurringTooltip) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showRecurringTooltip]);

  const loadSubscriptionPlans = () => {
    setIsLoading(true);
    subscriptionApi.callGetSubscriptionPlans(
      {},
      (message: string, response: any) => {
        console.log("Subscription plans loaded:", response);
        setIsLoading(false);
        setSubscriptionPlanData(response || null);
        setCurrentPlanData(response?.current_plan || null);

        // Set billing period toggle based on current plan type
        // type === 1 means monthly, type !== 1 means yearly
        if (response?.current_plan?.type === 1) {
          setBillingPeriod("monthly");
        } else if (response?.current_plan?.type) {
          setBillingPeriod("yearly");
        }
      },
      (error: any) => {
        console.error("Failed to load subscription plans:", error);
        toast.error(t("subscriptionPlans.messages.loadFailed"));
        setIsLoading(false);
      },
    );
  };

  // Helper function to check permissions for member users
  const canAccessMenu = (menuName: string): boolean => {
    // For members, strictly check permissions - only show if they have access
    if (userDetails?.user_type === "member") {
      return hasPermission(userPermissions, menuName);
    }

    // Non-members (employers/admins) have access to all menus
    return true;
  };

  // Check permissions for different actions
  const canReadSubscription = canAccessMenu("read_subscriptions");

  // If member doesn't have read permission, don't render the component
  if (userDetails?.user_type === "member" && !canReadSubscription) {
    return null;
  }

  const handleBackClick = () => {
    history.goBack();
  };

  const handleUpgradeClick = () => {
    // Scroll to other plans section
    if (otherPlansRef.current) {
      otherPlansRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handlePlanUpgradeSuccess = () => {
    // Refresh plan data after successful upgrade
    loadSubscriptionPlans();
  };

  const handleCancelSubscription = () => {
    setShowRecurringTooltip(false);
    setShowCancelModal(true);
  };

  const handleCancelModalClose = () => {
    setShowCancelModal(false);
  };

  const handleToggleRecurringTooltip = () => {
    setShowRecurringTooltip(!showRecurringTooltip);
  };

  const handleConfirmCancel = (reason: string) => {
    setIsCancelling(true);
    const payload = {
      subscription_id: currentPlanData?.subscription_plan_id,
      reason: reason,
    };

    subscriptionApi.callCancelSubscription(
      payload,
      (message: string) => {
        const cancelDate = moment(currentPlanData?.period_end).format("MMMM DD, YYYY");
        toast.success(`Your subscription has been cancelled and will remain valid until ${cancelDate}`);
        setShowCancelModal(false);
        setIsCancelling(false);
        // Refresh subscription data
        loadSubscriptionPlans();
      },
      (error: any) => {
        setIsCancelling(false);
        toast.error(error?.message || t("subscriptionPlans.messages.cancelError"));
      },
    );
  };

  return (
    <div className="dashboard-main member-page-wrapper">
      {/* Header Section */}
      <div className="drjob-page-header">
        <div className="drjob-header-left">
          <button className="back-button d-none" onClick={handleBackClick}>
            {t("subscriptionPlans.backButton")}
          </button>
          <h1 className="drjob-page-title">{t("subscriptionPlans.title")}</h1>
        </div>
      </div>

      <div className="drjob-member-list">
        {/* Current Plan Section - Active Subscription */}
        {/* {currentPlanData?.status == 1 && currentPlanData?.payment_details?.amount != 0 && ( */}
        {currentPlanData?.status == 1 && currentPlanData?.price != 0 && (
          <div className="drjob-table-container">
            <div className="subscription-current-section">
              <h3 className="section-title mb-3">{t("subscriptionPlans.currentSubscription.subscriptionInfo")}</h3>
              <div className="subscription-current-content">
                <div className="current-plan-left">
                  <div className="subscription-info-row">
                    <span className="subscription-info-label">
                      {t("subscriptionPlans.currentSubscription.nextPayment")}
                    </span>
                    <span className="subscription-info-value">
                      {moment(currentPlanData?.period_end).format("MMMM DD, YYYY")} —{" "}
                      {/* {currentPlanData?.payment_details?.currency} {currentPlanData?.payment_details?.amount} */}
                      USD {currentPlanData?.price}
                    </span>
                  </div>
                  <div className="subscription-info-row mt-2">
                    <span className="subscription-info-label">
                      {t("subscriptionPlans.currentSubscription.recurring")}
                    </span>
                    <span className="subscription-info-value position-relative">
                      <span className={`status-badge ${currentPlanData?.status === 1 ? "active" : ""}`}>
                        {t("subscriptionPlans.currentSubscription.active")}
                      </span>
                      <i
                        className="fa fa-chevron-down ms-2"
                        style={{ cursor: "pointer" }}
                        onClick={handleToggleRecurringTooltip}
                      ></i>

                      {showRecurringTooltip && (
                        <div
                          ref={tooltipRef}
                          className="recurring-tooltip"
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: "0",
                            marginTop: "8px",
                            backgroundColor: "#fff",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            padding: "12px 16px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            zIndex: 1000,
                            minWidth: "250px",
                            fontSize: "14px",
                            lineHeight: "1.5",
                          }}
                        >
                          <div>
                            {t("subscriptionPlans.currentSubscription.recurringTooltip")}{" "}
                            <span
                              onClick={handleCancelSubscription}
                              style={{
                                color: "#007bff",
                                cursor: "pointer",
                                textDecoration: "underline",
                              }}
                            >
                              {t("subscriptionPlans.currentSubscription.contactUs")}
                            </span>
                            .
                          </div>
                        </div>
                      )}
                    </span>
                  </div>
                </div>
                <div className="current-plan-right d-none">
                  <button
                    className="btn btn-danger cancel-subscription-btn"
                    onClick={handleCancelSubscription}
                    disabled={isCancelling}
                  >
                    {isCancelling ? (
                      <>
                        <i className="fa fa-spinner fa-spin me-2"></i>
                        {t("subscriptionPlans.currentSubscription.cancelling")}
                      </>
                    ) : (
                      <>
                        <i className="fa fa-times-circle me-2"></i>
                        {t("subscriptionPlans.currentSubscription.cancelButton")}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Current Plan Section - Cancelled Subscription */}
        {currentPlanData?.status == 2 && (
          <div className="drjob-table-container">
            <div className="subscription-current-section">
              <h3 className="section-title mb-3">{t("subscriptionPlans.currentPlan")}</h3>
              <div className="subscription-current-content">
                <div className="current-plan-left">
                  <div className="subscription-info-row">
                    <span className="subscription-info-label">
                      {t("subscriptionPlans.currentSubscription.planLabel")}{" "}
                    </span>
                    <span className="subscription-info-value">{currentPlanData?.title}</span>
                  </div>
                  {currentPlanData?.payment_details?.amount != 0 && (
                    <div className="subscription-info-row mt-2">
                      <span className="subscription-info-label">
                        {t("subscriptionPlans.currentSubscription.expiresOn")}
                      </span>
                      <span className="subscription-info-value">
                        {moment(currentPlanData?.period_end).format("MMMM DD, YYYY")}
                      </span>
                    </div>
                  )}
                </div>
                {currentPlanData?.payment_details?.amount != 0 && (
                  <div className="current-plan-right">
                    <button className="btn btn-secondary" disabled>
                      {t("subscriptionPlans.currentSubscription.cancelled")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Other Plans Section */}
        <div className="drjob-table-container" ref={otherPlansRef}>
          <div className="subscription-plans-section">
            <div className="toggle-switch-wrapper">
              <span className={`toggle-label ${billingPeriod === "monthly" ? "active" : ""}`}>
                {t("subscriptionPlans.billingPeriod.monthly")}
              </span>
              <div
                className="toggle-switch"
                onClick={() => setBillingPeriod(billingPeriod === "monthly" ? "yearly" : "monthly")}
              >
                <div className={`toggle-slider ${billingPeriod === "yearly" ? "yearly" : "monthly"}`}></div>
              </div>
              <span className={`toggle-label ${billingPeriod === "yearly" ? "active" : ""}`}>
                {t("subscriptionPlans.billingPeriod.yearly")}
              </span>
            </div>

            {/* Use the shared SubscriptionPlansGrid component */}
            <SubscriptionPlansGrid
              subscriptionPlanData={subscriptionPlanData}
              currentPlanData={currentPlanData}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              billingPeriod={billingPeriod}
              onPlanUpgradeSuccess={handlePlanUpgradeSuccess}
              showCurrentPlan={true}
              onLoadSubscriptionPlans={loadSubscriptionPlans}
            />
          </div>
        </div>
      </div>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <CancelSubscriptionModal
          isVisible={showCancelModal}
          onClose={handleCancelModalClose}
          onConfirm={handleConfirmCancel}
          isLoading={isCancelling}
          subscriptionPlanData={subscriptionPlanData}
          currentPlanData={currentPlanData}
          billingPeriod={billingPeriod}
        />
      )}
    </div>
  );
};

export default SubscriptionsMain;
